import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { theme } from "../theme";
import { fmtDateTime } from "../lib/format";

// The live deadline clock on a regulatory submission. Nothing here knows a statutory window: the
// due time is the submission's own dueAt (lib/regulatorySubmissions.ts deadlineFrom, or the
// incident notification rows lib/resilienceRules.ts derived), and the "due soon" threshold is the
// one the API serves (DUE_SOON_HOURS), so the chip and the attention items cannot disagree.

export type Urgency = "overdue" | "due_soon" | "on_time";

export const URGENCY_STYLE: Record<Urgency, { label: string; color: string; bg: string }> = {
  overdue:  { label: "Overdue",  color: theme.color.dangerText,  bg: theme.color.dangerBg },
  due_soon: { label: "Due soon", color: theme.color.warningText, bg: theme.color.warningBg },
  on_time:  { label: "On time",  color: theme.color.successText, bg: theme.color.successBg },
};

/** The current time, ticking every `intervalMs` so a countdown moves without a reload. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function urgencyAt(dueAt: string, now: number, dueSoonHours: number): Urgency {
  const hours = (new Date(dueAt).getTime() - now) / 3_600_000;
  if (hours < 0) return "overdue";
  if (hours <= dueSoonHours) return "due_soon";
  return "on_time";
}

/** "5h 12m left", "Overdue by 3h 4m", "6 days left" — hours and minutes under 48 hours. */
export function countdownText(dueAt: string, now: number): string {
  const diff = new Date(dueAt).getTime() - now;
  const abs = Math.abs(diff);
  const totalMinutes = Math.floor(abs / 60_000);
  let span: string;
  if (abs < 48 * 3_600_000) {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    span = h > 0 ? `${h}h ${m}m` : `${m}m`;
  } else {
    const d = Math.floor(abs / 86_400_000);
    span = `${d} day${d === 1 ? "" : "s"}`;
  }
  return diff < 0 ? `Overdue by ${span}` : `${span} left`;
}

export function CountdownChip({ dueAt, now, dueSoonHours, prefix }: {
  dueAt: string; now: number; dueSoonHours: number; prefix?: string;
}) {
  const style = URGENCY_STYLE[urgencyAt(dueAt, now, dueSoonHours)];
  return (
    <span
      title={`Due ${fmtDateTime(dueAt)}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700,
        padding: "2px 8px", borderRadius: 4, background: style.bg, color: style.color, whiteSpace: "nowrap",
      }}
    >
      <Clock size={11} />
      {prefix ? `${prefix} ` : ""}{countdownText(dueAt, now)}
    </span>
  );
}
